import React, {useEffect, useState} from 'react';
import * as Yup from 'yup';
import {Formik} from "formik";
import {
    FormGroup,
    Input,
    Label,
    Row,
    Col,
    CustomInput,
    Modal,
    ModalHeader,
    ModalBody,
    Alert,
    Spinner, FormFeedback
} from "reactstrap";
import {useDispatch, useSelector} from "react-redux";
import api from "../services/api";
import config from "../config";
import {FormContainer} from "react-authorize-net";
import {useHistory} from "react-router-dom";
import {toast} from "react-toastify";
import moment from "moment";
import {
    COVERAGE_CODE_CUSTOM_ORDER,
    DEPENDENT_TEMPLATE as dependentTemplate,
    PLAN_COVERAGE_STUDENT_AND_CHILDREN,
    PLAN_COVERAGE_STUDENT_AND_FAMILY,
    PLAN_COVERAGE_STUDENT_AND_SPOUSE,
    PLAN_COVERAGE_STUDENT_PLUS_ONE,
    SCHOOL_IDENTIFIER_SSN,
    SCHOOL_IDENTIFIER_STUDENT_CARD_ID
} from "../constants/generic";
import {receiveUser} from "../redux/auth/actions";
import {setToken} from "../services/auth";
import NumberFormat from 'react-number-format';
import Dependents from "./Dependents";
import {useMediaQuery} from 'react-responsive'
import {groupPlans, isDeltaCareBenefit} from "../services/generic";
import ReactTooltip from "react-tooltip";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";

const getDefaultPaymentMethod = (school) => {
    let paymentMethod = "check";
    if (school.options && school.options.payment_methods) {
        if (school.options.payment_methods.ach) {
            paymentMethod = "ach";
        } else if (school.options.payment_methods.credit_card) {
            paymentMethod = "credit-card";
        }
    }

    return paymentMethod;
}

const RegisterV2 = () => {
    const [plans, setPlans] = useState(null);
    const [selectedDurations, setSelectedDurations] = useState({});
    const [selectedPlans, setSelectedPlans] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [invalidPayment, setInvalidPayment] = useState(null);
    const [step, setStep] = useState(1);
    const [customFields, setCustomFields] = useState(null);
    const [errorBag, setErrorBag] = useState(null);
    const [loading, setLoading] = useState(true);
    const history = useHistory();
    const school = useSelector(state => state.school);
    const dispatch = useDispatch();
    const [dependents, setDependents] = useState({});
    const [validDependents, setValidDependents] = useState(false);
    const [touchedPaymentInput, setTouchedPaymentInput] = useState({cardCode: false, cardNumber: false, expDate: false})
    const isDesktop = useMediaQuery({
        query: '(min-device-width: 992px)'
    });
    const [facilityIds, setFacilityIds] = useState({});

    let registerInitialValues = {
        email: "",
        password: "",
        password_confirmation: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        gender: "",
        birthdate: "",
        phone_number: "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state: "",
        zip_code: "",
        school_id: school.id,
        custom_fields: {}
    };

    let registerValidationShape = {
        first_name: Yup.string().required(),
        last_name: Yup.string().required(),
        gender: Yup.string().required().oneOf(["male", "female", "other"]),
        birthdate: Yup.date().required(),
        email: Yup.string().required(),
        phone_number: Yup.string().required(),
        address_line_1: Yup.string().required(),
        city: Yup.string().required(),
        state: Yup.string().required(),
        zip_code: Yup.string().required().test('len', 'Must be exactly 5 characters', val => val && val.length === 5),
        password: Yup.string().required(),
        password_confirmation: Yup.string().required().oneOf([Yup.ref('password'), null])
    };

    if (school.options.identifier === SCHOOL_IDENTIFIER_STUDENT_CARD_ID) {
        registerValidationShape['student_card_id'] = Yup.string().required().max(25);
        registerInitialValues.student_card_id = "";
    } else {
        registerValidationShape['ssn'] = Yup.string().required();
        registerInitialValues.ssn = "";
    }

    const registerValidationSchema = Yup.object().shape(registerValidationShape);

    const register = (values, setSubmitting) => {
        setErrorBag(null);

        const parsed = {...values, birthdate: moment(values.birthdate).format('MM/DD/YYYY')};
        api.register(parsed).then(response => {
            dispatch(receiveUser(response.data.data));
            login(values.email, values.password).then(() => setStep(2));
        }).catch(error => {
            if (error.response.status === 422) {
                const {message, errors} = error.response.data;
                setErrorBag({message, errors});
            } else if (error.response.status === 500) {
                const {message} = error.response.data;
                setErrorBag({message, errors: []});
            }
        }).finally(() => setSubmitting(false));
    };

    const login = (email, password) => {
        return api.login(email, password).then(response => {
            setToken(response.data.access_token);
            toast("Your account has been created successfully.", {
                type: toast.TYPE.SUCCESS
            });
        });
    };

    let enrollmentInitialValues = {
        payment_method: getDefaultPaymentMethod(school),
        plan_ids: [],
        school_id: school.id,
        zip_code: "",
        ach: {
            account_number: "",
            account_type: "checking",
            routing_number: "",
            name_on_account: "",
            bank_name: ""
        }
    };

    let enrollmentValidationShape = {
        payment_method: Yup.string().required(),
        ach: Yup.object().shape({
            routing_number: Yup.string(),
            routing_number_confirmation: Yup.string()
                .oneOf([Yup.ref('routing_number')], "Routing number fields must match!"),
            account_number: Yup.string(),
            account_number_confirmation: Yup.string()
                .oneOf([Yup.ref('account_number')], "Account number fields must match!"),
        })
    };
    const enrollmentValidationSchema = Yup.object().shape(enrollmentValidationShape);

    useEffect(() => {
        (async () => {
            const plans = await api.fetchAvailablePlans().then(response => response.data.data);
            setPlans(groupPlans(plans));

            const customFields = await api.fetchCustomFields().then(response => response.data.data);
            setCustomFields(customFields);
            customFields.map(field => {
                registerInitialValues.custom_fields[field.value] = "10";
            });

            setLoading(false);
        })();
    }, []);

    const selectDuration = (benefit, durationName) => {
        setSelectedDurations({...selectedDurations, [benefit]: durationName});
        setSelectedPlans({...selectedPlans, [benefit]: ""});
        setPlans({...plans, [benefit]: (
            {...plans[benefit], carrier: plans[benefit].durations[durationName].carrier}
        )})

        delete dependents[benefit];
        setDependents({...dependents});
    };

    const selectCoverage = (benefit, planId) => {
        const filtered = plans[benefit].durations[selectedDurations[benefit]].coverages.filter(
            c => c.planId == planId
        );

        setSelectedPlans({
            ...selectedPlans,
            [benefit]: filtered.length ? filtered[0] : ""
        });

        const requireFacility = filtered[0]?.requireFacility;
        const coverageCode = filtered[0]?.coverageCode;

        if (requireFacility) {
            setFacilityIds({...facilityIds, [benefit]: ""});
        } else {
            delete facilityIds[benefit];
            setFacilityIds({...facilityIds});
        }

        let count = 0;
        if (coverageCode == PLAN_COVERAGE_STUDENT_AND_FAMILY) {
            count = null;
        } else if (coverageCode == PLAN_COVERAGE_STUDENT_AND_CHILDREN) {
            count = null;
        } else if (coverageCode == PLAN_COVERAGE_STUDENT_AND_SPOUSE) {
            count = 1;
        } else if (coverageCode == PLAN_COVERAGE_STUDENT_PLUS_ONE) {
            count = 1;
        }

        setDependents({
            ...dependents,
            [benefit]: {plan: filtered[0], count, list: count !== 0 ? [dependentTemplate] : []}
        });
    };

    const enroll = (values, setSubmitting) => {
        const submit = (values, setSubmitting) => {
            api.enroll({...values}).then(() => {
                toast("Congrats! You have successfully enrolled.", {
                    type: toast.TYPE.SUCCESS
                });

                history.push('/enrollment');
            }).catch((e) => {
                toast('Payment processing error!', {
                    type: toast.TYPE.ERROR
                });
                setSubmitting(false)
            });
        };

        let planIds = Object.values(selectedPlans).map(s => s.planId);
        values = {...values, plan_ids: planIds, facility_ids: facilityIds};

        let parsedDependents = {};
        Object.keys(values.dependents).forEach(k => {
            parsedDependents[k] = values.dependents[k].list;
        });
        values.dependents = parsedDependents;

        if (['check', 'university-union'].indexOf(values.payment_method) !== -1) {
            submit(values, setSubmitting);
        }

        if (values.payment_method === 'ach') {
            return submit({...values, payment: values.ach}, setSubmitting);
        }

        paymentHandleSubmit().then(r => {
            setInvalidPayment(false);
            const payment = r.opaqueData;
            submit({...values, payment}, setSubmitting);
        }).catch(() => {
            setInvalidPayment(true);
            setSubmitting(false)
        })
    };

    const toggleModal = () => setModalOpen(!modalOpen);

    let paymentHandleSubmit;

    let canSubmit = !!Object.values(selectedPlans).filter(a => a).length;

    let allDependents = [];
    Object.keys(dependents).forEach(k => {
        const {list} = dependents[k];
        allDependents = allDependents.concat(list);
    });
    let schema = Yup.array().of(
        Yup.object().shape({
            first_name: Yup.string().required(),
            last_name: Yup.string().required(),
            birthdate: Yup.date().required(),
            relation: Yup.string().required()
        })
    );
    schema.isValid(allDependents).then(valid => {
        setValidDependents(valid);
    });

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [step]);

    return (
        <div className="page">
            <div
                className="elementor-element elementor-element-60bf935 elementor-widget elementor-widget-heading animated fadeIn"
                data-id="60bf935" data-element_type="widget" data-settings="{&quot;_animation&quot;:&quot;fadeIn&quot;}"
                data-widget_type="heading.default">
                <div className="elementor-widget-container">
                    <h3 className="elementor-heading-title elementor-size-large">
                        {!!school.is_association ? 'New Member' : 'New Student'}
                    </h3>
                </div>
            </div>

            <div className="elementor-widget-container">
                <div className="elementor-divider">
                    <span className="elementor-divider-separator"/>
                </div>
            </div>

            {!school.is_association &&
            <div className="elementor-element elementor-widget elementor-widget-text-editor animated fadeIn">
                <div className="elementor-widget-container">
                    <div className="elementor-text-editor elementor-clearfix">
                        <p>
                            If you have previously enrolled in an insurance plan, please login as an <b>Existing
                            Student</b>.
                            If this is your first time enrolling, please create an account under <b>New
                            Student</b>. <br/>
                            <em>
                                Please sign up using your University email account.
                            </em>
                        </p>
                    </div>
                </div>
            </div>}

            {!loading &&
            <React.Fragment>
                {!Object.keys(plans).length ?
                    <p>
                        Open Enrollment is now closed. For more information or to see if you qualify for an exception,
                        please call (877) 247-8817.
                    </p> :
                    <React.Fragment>
                        <button className="elementor-button mb-3 btn btn-secondary" onClick={() => setModalOpen(true)}>
                            Register Now
                        </button>
                        <Modal isOpen={modalOpen} size="lg" toggle={toggleModal} backdrop="static"
                               id="modal-enrollment">
                            <ModalHeader toggle={toggleModal}>Enrollment</ModalHeader>
                            <ModalBody>
                                <div className="navigation">
                                    <div className="progressbar">
                                        <div className="progressbar-filled"
                                             style={
                                                 {width: step === 1 ? '33%' : (step === 2 ? '85%' : '100%')}
                                             }
                                        />
                                    </div>

                                    <div className={`step ${step > 0 ? 'active' : ''}`}>
                                        <div className="icon-wrapper">
                                            <img src={require('../assets/img/icon-step1.png')}/>
                                        </div>

                                        <h5>{!!school.is_association ? 'Member Details' : 'Student Details'}</h5>
                                    </div>

                                    <div className={`step ${step > 1 ? 'active' : ''}`}>
                                        <div className="icon-wrapper">
                                            <img src={require('../assets/img/icon-step2.png')}/>
                                        </div>

                                        <h5>Select Plan</h5>
                                    </div>

                                    <div className={`step ${step > 2 ? 'active' : ''}`}>
                                        <div className="icon-wrapper">
                                            <img src={require('../assets/img/icon-step3.png')}/>
                                        </div>

                                        <h5>Payment</h5>
                                    </div>
                                </div>

                                {errorBag &&
                                <Alert color="danger">
                                    <p className="mb-0">{errorBag.message}</p>
                                    {errorBag.errors &&
                                    <ul className="mb-0">
                                        {Object.values(errorBag.errors).map((e, i) => <li
                                            key={i}>{e}</li>)}
                                    </ul>}
                                </Alert>}

                                {/* {errorBag && errorBag.errors['student_card_id'] &&
                                <Alert color="danger">
                                    <p className="mb-0">
                                        A {!!school.is_association ? 'Member' : 'Student'} account has already been created using this {!!school.is_association ? 'Member' : 'Student'}
                                        ID.
                                        Forgot your password? Please
                                        contact xxx-xxx-xxxx if you need help accessing your account.
                                    </p>
                                </Alert>} */}

                                <Formik
                                    initialValues={registerInitialValues}
                                    isInitialValid={false}
                                    validationSchema={registerValidationSchema}
                                    onSubmit={(values, {setSubmitting}) => register(values, setSubmitting)}
                                >
                                    {props => {
                                        const {
                                            values, errors, touched, handleChange, handleBlur,
                                            handleSubmit, isSubmitting, setFieldValue,
                                            setFieldTouched
                                        } = props;

                                        return (
                                            <div className={`step1 ${step !== 1 && 'd-none'}`}>
                                                <FormGroup>
                                                    <Label for="first-name">Name</Label>
                                                    <Row>
                                                        <Col>
                                                            <FormGroup>
                                                                <Input type="text" name="first_name" id="first-name"
                                                                       onChange={handleChange}
                                                                       onBlur={handleBlur}
                                                                       value={values.first_name}
                                                                       invalid={!!(errors.first_name && touched.first_name)}
                                                                       className="elementor-field"
                                                                       placeholder="First"
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col>
                                                            <FormGroup>
                                                                <Input type="text" name="middle_name"
                                                                       id="middle-name"
                                                                       onChange={handleChange}
                                                                       onBlur={handleBlur}
                                                                       value={values.middle_name}
                                                                       invalid={!!(errors.middle_name && touched.middle_name)}
                                                                       className="elementor-field"
                                                                       placeholder="Middle"
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>
                                                            <FormGroup>
                                                                <Input type="text" name="last_name" id="last-name"
                                                                       onChange={handleChange}
                                                                       onBlur={handleBlur}
                                                                       value={values.last_name}
                                                                       invalid={!!(errors.last_name && touched.last_name)}
                                                                       className="elementor-field"
                                                                       placeholder="Last"
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col>
                                                            <FormGroup>
                                                                <div>
                                                                    <CustomInput type="radio" id="gender-male"
                                                                                 label="Male" value="male"
                                                                                 name="gender"
                                                                                 checked={values.gender === 'male'}
                                                                                 onChange={handleChange}
                                                                                 onBlur={handleBlur}
                                                                                 inline
                                                                    />
                                                                    <CustomInput type="radio" id="gender-female"
                                                                                 label="Female"
                                                                                 value="female"
                                                                                 name="gender"
                                                                                 checked={values.gender === 'female'}
                                                                                 onChange={handleChange}
                                                                                 onBlur={handleBlur}
                                                                                 inline
                                                                    />
                                                                    <CustomInput type="radio" id="gender-other"
                                                                                 label="Other"
                                                                                 value="other"
                                                                                 name="gender"
                                                                                 checked={values.gender === 'other'}
                                                                                 onChange={handleChange}
                                                                                 onBlur={handleBlur}
                                                                                 inline
                                                                    />
                                                                    {!!(errors.gender && touched.gender) &&
                                                                    <span className="text-danger">Select gender</span>}
                                                                </div>
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                </FormGroup>

                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <FormGroup>
                                                            <Label for="phone-number">Phone Number</Label>
                                                            <NumberFormat
                                                                value={values.phone_number}
                                                                customInput={Input}
                                                                allowEmptyFormatting
                                                                mask="#"
                                                                format="(###) ###-####"
                                                                onValueChange={values => {
                                                                    setFieldValue('phone_number', values.formattedValue);
                                                                }}
                                                                invalid={!!(errors.phone_number && touched.phone_number)}
                                                                onBlur={() => {
                                                                    setFieldTouched('phone_number')
                                                                }}
                                                            />
                                                        </FormGroup>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <FormGroup>
                                                            <Label for="birthdate">Birthdate</Label>
                                                            <NumberFormat
                                                                value={values.birthdate}
                                                                customInput={Input}
                                                                allowEmptyFormatting
                                                                mask="_"
                                                                format="##/##/####"
                                                                placeholder="MM/DD/YYYY"
                                                                onValueChange={values => {
                                                                    setFieldValue('birthdate', values.formattedValue);
                                                                }}
                                                                invalid={!!(errors.birthdate && touched.birthdate)}
                                                                onBlur={() => setFieldTouched('birthdate')}
                                                            />
                                                        </FormGroup>
                                                    </div>
                                                </div>


                                                <FormGroup>
                                                    <Label for="address">Address</Label>
                                                    <Input type="text" name="address_line_1" id="address-line-1"
                                                           onChange={handleChange}
                                                           onBlur={handleBlur}
                                                           value={values.address_line_1}
                                                           invalid={!!(errors.address_line_1 && touched.address_line_1)}
                                                           className="elementor-field"
                                                    />
                                                </FormGroup>

                                                <FormGroup>
                                                    <Label for="address">Address Line 2</Label>
                                                    <Input type="text" name="address_line_2" id="address-line-2"
                                                           onChange={handleChange}
                                                           onBlur={handleBlur}
                                                           value={values.address_line_2}
                                                           className="elementor-field"
                                                    />
                                                </FormGroup>

                                                <Row>
                                                    <Col>
                                                        <FormGroup>
                                                            <Label for="city">City</Label>
                                                            <Input type="text" name="city" id="city"
                                                                   onChange={handleChange}
                                                                   onBlur={handleBlur}
                                                                   value={values.city}
                                                                   invalid={!!(errors.city && touched.city)}
                                                                   className="elementor-field"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col>
                                                        <FormGroup>
                                                            <Label for="state">State</Label>
                                                            <Input type="text" name="state" id="state"
                                                                   onChange={handleChange}
                                                                   onBlur={handleBlur}
                                                                   value={values.state}
                                                                   invalid={!!(errors.state && touched.state)}
                                                                   className="elementor-field"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>

                                                <Row>
                                                    <Col className="col-6">
                                                        <FormGroup>
                                                            <Label for="zip-code">Zip Code</Label>
                                                            <Input type="text" name="zip_code" id="zip-code"
                                                                   onChange={handleChange}
                                                                   onBlur={handleBlur}
                                                                   value={values.zip_code}
                                                                   invalid={!!(errors.zip_code && touched.zip_code)}
                                                                   className="elementor-field"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>

                                                <hr/>

                                                {school.options.identifier === SCHOOL_IDENTIFIER_STUDENT_CARD_ID &&
                                                <FormGroup>
                                                    <Label
                                                        for="student-card-id">{!!school.is_association ? 'Member' : 'Student'} Id</Label>
                                                    <Input type="text" name="student_card_id" id="student-card-id"
                                                           onChange={handleChange}
                                                           onBlur={handleBlur}
                                                           value={values.student_card_id}
                                                           invalid={!!(errors.student_card_id && touched.student_card_id)}
                                                           className="elementor-field"
                                                    />
                                                </FormGroup>}

                                                {school.options.identifier === SCHOOL_IDENTIFIER_SSN &&
                                                <FormGroup>
                                                    <Label for="ssn">SSN</Label>
                                                    <Input type="text" name="ssn" id="ssn"
                                                           onChange={handleChange}
                                                           onBlur={handleBlur}
                                                           value={values.ssn}
                                                           invalid={!!(errors.ssn && touched.ssn)}
                                                           className="elementor-field"
                                                    />
                                                </FormGroup>}

                                                <FormGroup>
                                                    <Label for="email">Email</Label>
                                                    <Input type="email" name="email" id="email"
                                                           onChange={handleChange}
                                                           onBlur={handleBlur}
                                                           value={values.email}
                                                           invalid={!!(errors.email && touched.email)}
                                                           className="elementor-field"
                                                    />
                                                </FormGroup>

                                                <FormGroup>
                                                    <Label for="register-password">Password</Label>
                                                    <Row>
                                                        <Col>
                                                            <Input type="text" name="password"
                                                                   id="register-password"
                                                                   onChange={handleChange}
                                                                   onBlur={handleBlur}
                                                                   value={values.password}
                                                                   invalid={!!(errors.password && touched.password)}
                                                                   className="elementor-field"
                                                                   autoComplete="new-password"
                                                                   placeholder="Password"
                                                            />
                                                        </Col>
                                                        <Col>
                                                            <Input type="text" name="password_confirmation"
                                                                   id="password-confirmation"
                                                                   onChange={handleChange}
                                                                   onBlur={handleBlur}
                                                                   value={values.password_confirmation}
                                                                   invalid={!!(errors.password_confirmation && touched.password_confirmation)}
                                                                   className="elementor-field"
                                                                   autoComplete="new-password"
                                                                   placeholder="Confirmation"
                                                            />
                                                        </Col>
                                                    </Row>
                                                </FormGroup>

                                                {!!customFields.length &&
                                                <div className="mb-n3">
                                                    <hr/>

                                                    {customFields.map((customField, index) => {
                                                        return (
                                                            <FormGroup key={index}>
                                                                <Label for={`custom-field-${customField.value}`}>
                                                                    {customField.label}
                                                                </Label>
                                                                <Input id={`custom-field-${customField.value}`}
                                                                       className="elementor-field"
                                                                       onChange={e => {
                                                                           values.custom_fields[customField.value] = e.target.value;
                                                                       }}
                                                                       onBlur={handleBlur}
                                                                />
                                                            </FormGroup>
                                                        );
                                                    })}
                                                </div>}

                                                <div className="d-flex justify-content-end">
                                                    <div className="mt-5">
                                                        <button className="btn btn-primary"
                                                                onClick={handleSubmit}
                                                                disabled={isSubmitting}
                                                        >
                                                            Register
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }}
                                </Formik>

                                <Formik
                                    initialValues={enrollmentInitialValues}
                                    validationSchema={enrollmentValidationSchema}
                                    onSubmit={(values, {setSubmitting}) => enroll(values, setSubmitting)}
                                >
                                    {props => {
                                        const {
                                            values,
                                            handleChange,
                                            handleBlur,
                                            handleSubmit,
                                            isSubmitting,
                                            errors,
                                            setFieldValue
                                        } = props;

                                        values.dependents = dependents;
                                        let subtotal = 0;

                                        return (
                                            <React.Fragment>
                                                <div className={`step2 ${step !== 2 && 'd-none'}`}>
                                                    {isDesktop &&
                                                    <div className="table-responsive mb-3">
                                                        <table className="table table-striped table-bordered table-sm">
                                                            <thead>
                                                            <tr>
                                                                <th className="text-center" nowrap="true">Plan</th>
                                                                <th className="text-center" nowrap="true">Carrier</th>
                                                                <th className="text-center" nowrap="true">Plan Type &
                                                                    Duration
                                                                </th>
                                                                <th className="text-center" nowrap="true">Coverage</th>
                                                                <th className="text-center" nowrap="true">Total Cost</th>
                                                            </tr>
                                                            </thead>
                                                            <tbody>
                                                            {Object.keys(plans).map(benefit => {
                                                                let premium = "";
                                                                if (selectedPlans[benefit]) {
                                                                    premium = selectedPlans[benefit].adjustedPremium[values.payment_method];
                                                                }

                                                                if (premium) subtotal += premium;

                                                                let coverages = [];
                                                                if (selectedDurations[benefit]) {
                                                                    coverages = plans[benefit].durations[selectedDurations[benefit]].coverages.sort((a, b) => {
                                                                        return COVERAGE_CODE_CUSTOM_ORDER.indexOf(a.coverageCode) - COVERAGE_CODE_CUSTOM_ORDER.indexOf(b.coverageCode)
                                                                    });
                                                                }

                                                                return (
                                                                    <tr key={benefit}>
                                                                        <td className="align-middle text-center">{benefit}</td>
                                                                        <td className="align-middle text-center">{plans[benefit]?.carrier || ''}</td>
                                                                        <td style={{minWidth: 225}}>
                                                                            <select
                                                                                className="form-control form-control-sm"
                                                                                onChange={(e) => {
                                                                                    selectDuration(benefit, e.target.value);
                                                                                }}
                                                                                value={selectedDurations[benefit]}
                                                                            >
                                                                                <option value="">Select
                                                                                    Plan
                                                                                    Type and
                                                                                    Duration
                                                                                </option>
                                                                                {Object.keys(plans[benefit].durations).map(duration => {
                                                                                    return (
                                                                                        <option
                                                                                            value={duration}
                                                                                            key={duration}>
                                                                                            {duration}
                                                                                        </option>
                                                                                    );
                                                                                })}
                                                                            </select>
                                                                        </td>
                                                                        <td style={{minWidth: 125}}>
                                                                            <select
                                                                                className="form-control form-control-sm"
                                                                                onChange={e => {
                                                                                    selectCoverage(benefit, e.target.value)
                                                                                }}
                                                                                value={selectedPlans[benefit]?.planId || ""}
                                                                            >
                                                                                <option value="">
                                                                                    Select Coverage
                                                                                </option>

                                                                                {coverages.map((coverage, i) => {
                                                                                    return (
                                                                                        <option
                                                                                            value={coverage.planId}
                                                                                            key={i}
                                                                                        >
                                                                                            {!!school.is_association ? coverage.coverage.replace('Student', 'Member') : coverage.coverage}
                                                                                        </option>
                                                                                    );
                                                                                })}
                                                                            </select>
                                                                        </td>
                                                                        <td style={{minWidth: 50}}
                                                                            className="text-center align-middle"
                                                                        >
                                                                            {!!premium && `$${premium.toFixed(2)}`}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                            <tr>
                                                                <td className="text-right" colSpan={4}>Total</td>
                                                                <td className="text-center">
                                                                    <b>${subtotal.toFixed(2)}</b>
                                                                </td>
                                                            </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>}

                                                    {!isDesktop &&
                                                    <div className="mobile">
                                                        {Object.keys(plans).map(benefit => {
                                                            let premium = "";
                                                            if (selectedPlans[benefit]) {
                                                                premium = selectedPlans[benefit].adjustedPremium[values.payment_method];
                                                            }

                                                            if (premium) subtotal += premium;

                                                            let coverages = [];
                                                            if (selectedDurations[benefit]) {
                                                                coverages = plans[benefit].durations[selectedDurations[benefit]].coverages.sort((a, b) => {
                                                                    return COVERAGE_CODE_CUSTOM_ORDER.indexOf(a.coverageCode) - COVERAGE_CODE_CUSTOM_ORDER.indexOf(b.coverageCode)
                                                                });
                                                            }

                                                            return (
                                                                <React.Fragment key={benefit}>
                                                                    <div className="mb-3">
                                                                        <h3 className="font-weight-bold">{benefit}</h3>

                                                                        <div className="form-group">
                                                                            <label htmlFor="">
                                                                                Plan Type and Duration
                                                                            </label>
                                                                            <select
                                                                                className="form-control form-control-sm"
                                                                                onChange={(e) => {
                                                                                    selectDuration(benefit, e.target.value);
                                                                                }}
                                                                                value={selectedDurations[benefit]}
                                                                            >
                                                                                <option value="">
                                                                                    Select Plan Type and
                                                                                    Duration
                                                                                </option>
                                                                                {Object.keys(plans[benefit].durations).map(duration => {
                                                                                    return (
                                                                                        <option value={duration}
                                                                                                key={duration}>
                                                                                            {duration}
                                                                                        </option>
                                                                                    );
                                                                                })}
                                                                            </select>
                                                                        </div>

                                                                        <div className="form-group">
                                                                            <label htmlFor="">
                                                                                Coverage
                                                                            </label>
                                                                            <select
                                                                                className="form-control form-control-sm"
                                                                                onChange={e => {
                                                                                    selectCoverage(benefit, e.target.value)
                                                                                }}
                                                                                value={selectedPlans[benefit]?.planId || ""}
                                                                            >
                                                                                <option value="">
                                                                                    Select Coverage
                                                                                </option>

                                                                                {coverages.map((coverage, i) => {
                                                                                    return (
                                                                                        <option
                                                                                            value={coverage.planId}
                                                                                            key={i}
                                                                                        >
                                                                                            {coverage.coverage}
                                                                                        </option>
                                                                                    );
                                                                                })}
                                                                            </select>
                                                                        </div>

                                                                        <b>Plan
                                                                            Cost:</b> {!!premium && `$${premium.toFixed(2)}`}
                                                                    </div>
                                                                </React.Fragment>
                                                            );
                                                        })}

                                                        <hr/>

                                                        <div className="mb-3">
                                                            <b>Total:</b> {!!subtotal && `$${subtotal.toFixed(2)}`}
                                                        </div>
                                                    </div>}

                                                    <p className="mb-3 text-muted">
                                                        An administration fee is included in the total plan cost.
                                                    </p>

                                                    <div className="row">
                                                        {Object.keys(facilityIds).map(k => {
                                                            return (
                                                                <div className="col-auto" key={k}>
                                                                    <div className="form-group" style={{marginBottom: isDeltaCareBenefit(k) ? 24 : 0}}>
                                                                        <label>DeltaCare Facility ID</label>
                                                                        <input type="text" value={facilityIds[k]}
                                                                               className="form-control"
                                                                               onChange={e => {
                                                                                   setFacilityIds({
                                                                                       ...facilityIds,
                                                                                       [k]: e.target.value
                                                                                   });
                                                                               }}
                                                                        />
                                                                    </div>

                                                                    {isDeltaCareBenefit(k) &&
                                                                        <a href="https://www.deltadentalil.com/find-a-provider/dental/"
                                                                           target="_blank"
                                                                           rel="noopener noreferrer" // It's good practice to add rel="noopener noreferrer" for security reasons
                                                                           style={{fontWeight: 'bold', textDecoration: 'underline'}}
                                                                        >
                                                                            Click here to find a DeltaCare facility.
                                                                        </a>}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>


                                                    <Dependents dependents={values.dependents}
                                                                onChange={dependents => setDependents(dependents)}
                                                    />

                                                    <div className="d-flex justify-content-end">
                                                        <div className="mt-5">
                                                            <button className="btn btn-primary"
                                                                    onClick={() => {
                                                                        if (!subtotal) {
                                                                            setFieldValue('payment_method', 'university-union')
                                                                        }
                                                                        setStep(step + 1)
                                                                    }}
                                                                    disabled={!canSubmit || !validDependents}
                                                            >
                                                                Next
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={`step3 ${step !== 3 && 'd-none'}`}>
                                                    <div className="mb-3">
                                                        <p className="mb-1">
                                                            Total: <b>${subtotal.toFixed(2)}</b>
                                                        </p>
                                                        <p className="mb-0 text-muted">
                                                            An administration fee is included in the total plan cost.
                                                        </p>
                                                    </div>

                                                    <React.Fragment>
                                                        <FormGroup className="mb-0">
                                                            <Label for="payment-method-credit-card">
                                                                Payment Method
                                                            </Label>
                                                            <div>
                                                                {!!subtotal && school.options.payment_methods?.ach &&
                                                                    <CustomInput
                                                                        type="radio"
                                                                        id="payment-method-ach"
                                                                        label="ACH"
                                                                        value="ach"
                                                                        name="payment_method"
                                                                        checked={values.payment_method === 'ach'}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        inline
                                                                    />}

                                                                {!!subtotal && school.options.payment_methods?.credit_card &&
                                                                    <CustomInput
                                                                        type="radio"
                                                                        id="payment-method-credit-card"
                                                                        label="Credit Card"
                                                                        value="credit-card"
                                                                        name="payment_method"
                                                                        checked={values.payment_method === 'credit-card'}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        inline
                                                                    />}

                                                                {!!subtotal && school.options.payment_methods?.check &&
                                                                    <CustomInput
                                                                        type="radio"
                                                                        id="payment-method-check"
                                                                        label="Check"
                                                                        value="check"
                                                                        name="payment_method"
                                                                        checked={values.payment_method === 'check'}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        inline
                                                                    />}

                                                                {!subtotal && school.options.payment_methods?.university_union &&
                                                                    <CustomInput
                                                                        type="radio"
                                                                        id="payment-method-university-union"
                                                                        label="University Sponsored – no payment necessary"
                                                                        value="university-union"
                                                                        name="payment_method"
                                                                        checked={values.payment_method === 'university-union'}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        inline
                                                                    />}
                                                            </div>
                                                        </FormGroup>

                                                        {values.payment_method === 'check' &&
                                                            <p className="mb-0 mt-3">
                                                                Your plan(s) will be activated once the check is received by mail and processed.
                                                            </p>}

                                                        {values.payment_method === 'university-union' &&
                                                            <p className="mb-0 mt-3">
                                                                Please review your confirmation email for directions
                                                                regarding how to use your benefits.
                                                            </p>}

                                                        {values.payment_method === 'ach' &&
                                                            <div>
                                                                <div className="row mt-3">
                                                                    <div className="col-md-4">
                                                                        <div className="form-group">
                                                                            <label>Name on Account</label>
                                                                            <Input type="text"
                                                                                   id="payment-method-ach-name-on-account"
                                                                                   label="Name on Account"
                                                                                   value={values.ach.name_on_account}
                                                                                   name="ach[name_on_account]"
                                                                                   onChange={handleChange}
                                                                                   onBlur={handleBlur}
                                                                                   maxLength="22"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-4">
                                                                        <div className="form-group">
                                                                            <label>Bank Name</label>
                                                                            <Input type="text"
                                                                                   id="payment-method-ach-bank-name"
                                                                                   label="Bank Name"
                                                                                   value={values.ach.bank_name}
                                                                                   name="ach[bank_name]"
                                                                                   onChange={handleChange}
                                                                                   onBlur={handleBlur}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="row">
                                                                    <div className="col-md-4">
                                                                        <div className="form-group">
                                                                            <label>Routing Number</label>
                                                                            <Input type="text"
                                                                                   id="payment-method-ach-routing-number"
                                                                                   label="Routing Number"
                                                                                   value={values.ach.routing_number}
                                                                                   name="ach[routing_number]"
                                                                                   onChange={handleChange}
                                                                                   onBlur={handleBlur}
                                                                                   maxLength="9"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-4">
                                                                        <div className="form-group">
                                                                            <label>Routing Number Confirmation</label>
                                                                            <Input type="text"
                                                                                   id="payment-method-ach-routing-number-confirmation"
                                                                                   label="Routing Number Confirmation"
                                                                                   value={values.ach.routing_number_confirmation}
                                                                                   name="ach[routing_number_confirmation]"
                                                                                   onChange={handleChange}
                                                                                   onBlur={handleBlur}
                                                                                   maxLength="9"
                                                                                   invalid={errors.ach?.routing_number_confirmation}
                                                                            />
                                                                            <FormFeedback invalid={true}>{errors.ach?.routing_number_confirmation}</FormFeedback>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="row">
                                                                    <div className="col-md-4">
                                                                        <div className="form-group">
                                                                            <label>Account Number</label>
                                                                            <Input type="text"
                                                                                   id="payment-method-ach-account-number"
                                                                                   label="Account Number"
                                                                                   value={values.ach.account_number}
                                                                                   name="ach[account_number]"
                                                                                   onChange={handleChange}
                                                                                   onBlur={handleBlur}
                                                                                   maxLength="17"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-4">
                                                                        <div className="form-group">
                                                                            <label>Account Number Confirmation</label>
                                                                            <Input type="text"
                                                                                   id="payment-method-ach-account-number-confirmation"
                                                                                   label="Account Number Confirmation"
                                                                                   value={values.ach.account_number_confirmation}
                                                                                   name="ach[account_number_confirmation]"
                                                                                   onChange={handleChange}
                                                                                   onBlur={handleBlur}
                                                                                   maxLength="17"
                                                                                   invalid={errors.ach?.account_number_confirmation}
                                                                            />
                                                                            <FormFeedback invalid={true}>{errors.ach?.account_number_confirmation}</FormFeedback>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="row">
                                                                    <div className="col-md-4">
                                                                        <div className="form-group">
                                                                            <label>Account Type</label>
                                                                            <select
                                                                                className="form-control"
                                                                                onChange={handleChange}
                                                                                value={values.ach.account_type}
                                                                                name="ach[account_type]"
                                                                            >
                                                                                <option value="checking">Checking</option>
                                                                                <option value="savings">Savings</option>
                                                                                <option value="businessChecking">
                                                                                    Business Checking
                                                                                </option>
                                                                            </select>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <p className="mt-3">Sample Check</p>
                                                                <img src={`${config.apiUrl}/student/static/check-example.jpg`} width="70%"/>
                                                            </div>}

                                                        {values.payment_method === 'credit-card' &&
                                                            <div className={`mt-3 ${invalidPayment && 'invalid-payment'}`}>
                                                                <FormContainer
                                                                    clientKey={config.authorizeNet.clientKey}
                                                                    apiLoginId={config.authorizeNet.apiLoginId}
                                                                    amount={Math.round(subtotal * 100) / 100}
                                                                    environment={config.authorizeNet.environment}
                                                                    render={paymentProps => {
                                                                        const {
                                                                            values: paymentValues,
                                                                            handleChange: paymentHandleChange,
                                                                            validationErrors: validInputs
                                                                        } = paymentProps;

                                                                        paymentHandleSubmit = paymentProps.handleSubmit;

                                                                        return (
                                                                            <React.Fragment>
                                                                                <div className="text-right">
                                                                                    <img
                                                                                        src={require('../assets/img/credit-card-logos.png')}
                                                                                        className="mb-3"
                                                                                    />
                                                                                </div>
                                                                                <div className="row">
                                                                                    <div className="col-md-6">
                                                                                        <div className="form-group">
                                                                                            <Label for="card-number">
                                                                                                Card Number
                                                                                            </Label>
                                                                                            <Input type="text"
                                                                                                   id="card-number"
                                                                                                   onChange={e => {
                                                                                                       e.persist();
                                                                                                       paymentHandleChange('cardNumber', e)
                                                                                                   }}
                                                                                                   onBlur={() => {
                                                                                                       setTouchedPaymentInput({
                                                                                                           ...touchedPaymentInput,
                                                                                                           cardNumber: true
                                                                                                       })
                                                                                                   }}
                                                                                                   value={paymentValues.cardNumber}
                                                                                                   invalid={touchedPaymentInput.cardNumber && !validInputs.cardNumber && !isSubmitting}
                                                                                                   placeholder="1234567890"
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="col-md-3">
                                                                                        <div className="form-group">
                                                                                            <Label for="card-code">
                                                                                                CVC Code
                                                                                            </Label>
                                                                                            <Input type="text"
                                                                                                   id="card-code"
                                                                                                   onChange={e => {
                                                                                                       e.persist();
                                                                                                       paymentHandleChange('cardCode', e);
                                                                                                   }}
                                                                                                   onBlur={() => {
                                                                                                       setTouchedPaymentInput({
                                                                                                           ...touchedPaymentInput,
                                                                                                           cardCode: true
                                                                                                       })
                                                                                                   }}
                                                                                                   value={paymentValues.cardCode}
                                                                                                   invalid={touchedPaymentInput.cardCode && !validInputs.cardCode && !isSubmitting}
                                                                                                   placeholder="xxx"
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="col-md-3">
                                                                                        <div className="form-group">
                                                                                            <Label for="exp-date">
                                                                                                Exp. Date
                                                                                            </Label>
                                                                                            <Input type="text"
                                                                                                   id="exp-date"
                                                                                                   onChange={e => {
                                                                                                       e.persist();
                                                                                                       paymentHandleChange('expDate', e)
                                                                                                   }}
                                                                                                   onBlur={() => {
                                                                                                       setTouchedPaymentInput({
                                                                                                           ...touchedPaymentInput,
                                                                                                           expDate: true
                                                                                                       })
                                                                                                   }}
                                                                                                   value={paymentValues.expDate}
                                                                                                   invalid={touchedPaymentInput.expDate && !validInputs.expDate && !isSubmitting}
                                                                                                   placeholder="MM/YY"
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </React.Fragment>
                                                                        )
                                                                    }}
                                                                />

                                                                <div className="row mb-n3">
                                                                    <div className="col-4">
                                                                        <div className="form-group">
                                                                            <label
                                                                                data-tip="Please enter the billing zip code associated with the credit or debit card.">
                                                                                Zip Code <FontAwesomeIcon
                                                                                icon={faInfoCircle}/>
                                                                            </label>
                                                                            <Input type="text" name="zip_code"
                                                                                   value={values.zip_code}
                                                                                   className="form-control"
                                                                                   onChange={handleChange}
                                                                                   placeholder="xxxxx"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>}

                                                        <div className="d-flex justify-content-end">
                                                            <div className="mt-5">
                                                                <button className="btn btn-primary"
                                                                        onClick={() => {
                                                                            setFieldValue('payment_method', getDefaultPaymentMethod(school));
                                                                            setStep(step - 1)
                                                                        }}
                                                                >
                                                                    Prev
                                                                </button>
                                                                {' '}

                                                                {canSubmit &&
                                                                    <button type="button" onClick={handleSubmit}
                                                                            className="elementor-button btn btn-secondary"
                                                                            disabled={isSubmitting}
                                                                    >
                                                                        Submit
                                                                        {isSubmitting &&
                                                                            <React.Fragment>
                                                                                {' '}
                                                                                <Spinner animation="border" size="sm"/>
                                                                            </React.Fragment>}
                                                                    </button>}
                                                            </div>
                                                        </div>
                                                    </React.Fragment>
                                                </div>
                                            </React.Fragment>
                                        );
                                    }}
                                </Formik>
                            </ModalBody>
                        </Modal>
                    </React.Fragment>}
            </React.Fragment>}
        </div>
    );
};

export default RegisterV2;
